import React, { useState, useEffect, Suspense, lazy } from "react";
import { HelmetProvider } from "react-helmet-async";
import Container from "@material-ui/core/Container"
import CssBaseline from "@material-ui/core/CssBaseline"
import CircularProgress from "@material-ui/core/CircularProgress"
import { createTheme, ThemeProvider } from "@material-ui/core/styles";
import { BrowserRouter, Switch as SwitchRouter, Route, useLocation } from "react-router-dom";

const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
};
import { GoogleOAuthProvider } from "@react-oauth/google";
import AdSenseGuard from "./components/GoogleAd/AdSenseGuard";

// Eager: shell + the two highest-traffic pages
import Navbar from "./components/Navbar/Navbar";
import Footer from "./components/Footer/Footer";
import CookieBanner from "./components/CookieBanner/CookieBanner";
import LandingPage from "./components/LandingPage/LandingPage";
import Courses from "./components/Courses/Courses";
import CourseDetails from "./components/Courses/Course/CourseDetails";
import NotFound from "./components/NotFound/NotFound";

// Lazy: everything else is split into separate chunks
const PostDetails = lazy(() => import("./components/PostDetails/PostDetails"));
const Profile = lazy(() => import("./components/Profile/Profile"));
const UserProfile = lazy(() => import("./components/Profile/UserProfile"));
const Home = lazy(() => import("./components/Home/Home"));
const Auth = lazy(() => import("./components/Auth/Auth"));
const AddCourse = lazy(() => import("./components/AddCourse/AddCourse"));
const EditCourse = lazy(() => import("./components/EditCourse/EditCourse"));
const Contact = lazy(() => import("./components/Contact/Contact"));
const Api = lazy(() => import("./components/API/Api"));
const Dashboard = lazy(() => import("./components/Dashboard/Dashboard"));
const Privacy = lazy(() => import("./components/Privacy/Privacy"));
const Terms = lazy(() => import("./components/Terms/Terms"));
const About = lazy(() => import("./components/About/About"));
const HandicapCalculator = lazy(() => import("./components/HandicapCalculator/HandicapCalculator"));
const Leaderboard = lazy(() => import("./components/Leaderboard/Leaderboard"));
const Glossary = lazy(() => import("./components/Glossary/Glossary"));
const GolfTips = lazy(() => import("./components/GolfTips/GolfTips"));
const StateRankings = lazy(() => import("./components/StateRankings/StateRankings"));
const CoursesHub = lazy(() => import("./components/CoursesHub/CoursesHub"));

const RouteFallback = () => (
  <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
    <CircularProgress />
  </div>
);

const theme = createTheme({
  palette: {
    type: 'light',
    primary: {
      light: '#2E6B52',
      main: '#0F3D2E',
      dark: '#092A1F',
      contrastText: '#F7F4EC',
    },
    secondary: {
      light: '#D4B96A',
      main: '#A8863A',
      dark: '#8A6D2C',
      contrastText: '#1E2A25',
    },
    error: {
      main: '#C0392B',
    },
    background: {
      default: '#F7F4EC',
      paper: '#FFFEFA',
    },
    text: {
      primary: '#1E2A25',
      secondary: '#5C6B63',
    },
    divider: 'rgba(15, 61, 46, 0.14)',
  },
  typography: {
    fontFamily: '"Inter", "Helvetica", "Arial", sans-serif',
    h1: { fontFamily: '"Playfair Display", Georgia, serif', fontWeight: 700 },
    h2: { fontFamily: '"Playfair Display", Georgia, serif', fontWeight: 700 },
    h3: { fontFamily: '"Playfair Display", Georgia, serif', fontWeight: 700 },
    h4: { fontFamily: '"Playfair Display", Georgia, serif', fontWeight: 700 },
    h5: { fontFamily: '"Playfair Display", Georgia, serif', fontWeight: 600 },
    h6: { fontFamily: '"Playfair Display", Georgia, serif', fontWeight: 600 },
    button: { textTransform: 'none', fontWeight: 600 },
  },
  shape: {
    borderRadius: 10,
  },
  overrides: {
    MuiCssBaseline: {
      '@global': {
        body: {
          backgroundColor: '#F7F4EC',
        },
      },
    },
    MuiButton: {
      root: {
        borderRadius: 8,
      },
      containedPrimary: {
        boxShadow: 'none',
        '&:hover': {
          boxShadow: '0 4px 14px rgba(15, 61, 46, 0.25)',
        },
      },
    },
    MuiPaper: {
      rounded: {
        borderRadius: 10,
      },
    },
    MuiChip: {
      root: {
        fontWeight: 500,
      },
    },
    MuiLink: {
      root: {
        fontWeight: 500,
      },
    },
  },
});

const App = () => {
  const [deviceType, setDeviceType] = useState("");

  useEffect(() => {
    if (/Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini|Windows Phone/i.test(navigator.userAgent)) {
      setDeviceType("mobile");
    } else {
      setDeviceType("desktop");
    }
  }, []);

  return (
    <HelmetProvider>
    <ThemeProvider theme={theme}>
      <BrowserRouter>
        <ScrollToTop />
        <AdSenseGuard />
        <CssBaseline />
        <GoogleOAuthProvider clientId={process.env.REACT_APP_GOOGLE_ID}>
          <Navbar />
          <Container maxWidth="lg" className="content" component="main">
            <Suspense fallback={<RouteFallback />}>
            <SwitchRouter>
              <Route path="/" exact component={LandingPage} />
              <Route path="/posts" exact component={Home} />
              <Route path="/posts/search" exact component={Home} />
              <Route path="/posts/:id" component={() => <PostDetails deviceType={deviceType} />} />
              <Route path="/courses" exact component={CoursesHub} />
              <Route path="/courses/search" exact component={Courses} />
              <Route path="/courses/add" exact component={AddCourse} />
              <Route path="/course/update/:id" exact component={EditCourse} />
              <Route path="/course/:id" exact component={() => <CourseDetails deviceType={deviceType} />} />
              <Route path="/api" exact component={Api} />
              <Route path="/contact" exact component={Contact} />
              <Route path="/about" exact component={About} />
              <Route path="/privacy-policy" exact component={Privacy} />
              <Route path="/terms" exact component={Terms} />
              <Route path="/handicap-calculator" exact component={HandicapCalculator} />
              <Route path="/leaderboard" exact component={Leaderboard} />
              <Route path="/glossary" exact component={Glossary} />
              <Route path="/golf-tips" exact component={GolfTips} />
              <Route path="/golf-tips/:slug" exact component={GolfTips} />
              <Route path="/best-golf-courses/:state" exact component={StateRankings} />
              <Route path="/auth" exact component={() => <Auth deviceType={deviceType} />} />
              <Route path="/profile" exact component={Profile} />
              <Route path="/user-profile" exact component={UserProfile} />
              <Route path="/dashboard" exact component={Dashboard} />
              <Route component={NotFound} />
            </SwitchRouter>
            </Suspense>
          </Container>
          <Footer />
          <CookieBanner />
        </GoogleOAuthProvider>
      </BrowserRouter>
    </ThemeProvider>
    </HelmetProvider>
  );
};

export default App;
